/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { useSelector } from 'react-redux';
import mbpLogger from '@module-logger/mbp';
import {
    array, bool, func, string,
} from 'prop-types';

import { getSSRDeviceType } from '../../../../../../state/ducks/App/App-Selectors';

import useSalesforceCollectiontRecommendationQuery from '../../../../../helpers/Personalization/useSalesforceCollectiontRecommendationQuery';
import PLARelatedCollection from './PLARelatedCollection';
import PLAPDPIcon from './PLAPDPIcon';
import noop from '../../../../../helpers/noop';

export default function PLACollectionRecsContainer({
    partNumber, productName, skuData, isCategoryBasedQuery, trackClickThrough,
}) {
    const ssrDeviceType = useSelector(getSSRDeviceType);
    const isMobile = ssrDeviceType.toLowerCase() === 'mobile';
    // adds sympathy collection recs
    const isSympathySku = skuData?.some((sku) => sku?.sympathyValue === '1' || sku?.sympathyValue === '2');

    const {
        loading, error, data, variables,
    } = useSalesforceCollectiontRecommendationQuery({
        queryName: 'PLACollectionRecsContainer',
        campaignFeature: 'Collection PLA Recs',
        isSympathySku,
        targeting: {
            pageType: 'product',
        },
        filterByZipCode: true,
        optInHoldoutParticipation: true,
        resolveIdentity: true,
    });

    if (loading || variables?.skip) return null;

    if (error) {
        mbpLogger.logError({
            function: 'PLACollectionRecsContainer.js',
            message: 'Error loading data from saleforce api',
            appName: process.env.npm_package_name,
            module: 'PLA',
            jsError: error,
        });
    }

    if (data?.collectionRecommendations?.collections?.length > 0) {
        const collectionData = data?.collectionRecommendations?.collections;

        // 6805; Nupv3 styling too complicated to mimic, easier to do a data restructuring so the same component can be re-used
        const collectionsRestructurer = (sfData) => {
            const entries = sfData?.collectionRecommendations?.collections?.map((elem) => {
                const imagePath = elem?.image?.path ? `${elem?.image?.path}${elem?.image?.name}x.jpg` : '';

                const entry = {
                    title: elem?.name || '',
                    linking: {
                        link: {
                            title: elem?.name  || '',
                            href: elem?.path  || '',
                        },
                    },
                    image: {
                        title: elem?.image?.name || '',
                        url: imagePath,
                    },
                };
                return entry;
            });

            const result = {
                header_section: {},
                header_styling: {
                    font_color: {
                        colorTheme: 'custom',
                        color: '#ebebeb',
                        colorTransparency: '0',
                        colorDescription: '',
                        version: '1',
                    },
                },
                styling: {
                    presentation_style: 'circular',
                    has_inset_border: true,
                    mobile_presentation: 'scrollbar',
                    heading_font: '',
                    title_font: 'font-family: LatoMedium, sans-serif;',
                    title_font_color: {
                        colorTheme: 'custom',
                        color: '#000000',
                        colorTransparency: '0',
                        colorDescription: '',
                        version: '1',
                    },
                },
            };

            result.entries = entries;

            return result;
        };

        const dataForNupComponent = collectionsRestructurer(data);
        if (isMobile && dataForNupComponent?.entries?.length > 0) {
            return (
                <PLAPDPIcon
                    blockData={dataForNupComponent}
                    trackClickThrough={trackClickThrough}
                    isPersonalizedProp
                />
            );
        }

        return (
            <PLARelatedCollection
                data={collectionData}
                trackClickThrough={trackClickThrough}
                isPersonalizedProp
            />
        );
    }

    return null;
}

PLACollectionRecsContainer.propTypes = {
    partNumber: string.isRequired,
    productName: array.isRequired,
    skuData: array.isRequired,
    isCategoryBasedQuery: bool,
    trackClickThrough: func,
};

PLACollectionRecsContainer.defaultProps = {
    isCategoryBasedQuery: false,
    trackClickThrough: noop,
};
